import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Block 14 «Раскрой» — цифровая клавиатура (PR #6, CUT-2.4). Порт TRD VII.3
 * (`04-SC-…-v3-spec.md`) + раскладка/поведение `js/numeric-keyboard.js` (PomKeyboard):
 * сетка 4 столбца (1 2 3 ↑ / 4 5 6 ↓ / 7 8 9 ← / . 0 / → / Del OK),
 * long-press на Del = очистить, навигация по ячейкам через onNavigate.
 *
 * Презентационный: значение/мутации — снаружи (контроллер в PartsTab). Всплывает
 * ВЕЗДЕ по фокусу `.kb-input` — и на touch, и на ПК (PR #7-FIX, решение владельца
 * 09.06.2026, как прототип v3.14; §IX.X #3 переопределён, B-219). На ПК физическая
 * клавиатура тоже работает (kb-input `inputmode="none"` подавляет лишь мобильную системную).
 *
 * PR #8a-MOBILE (правка #9, решение владельца 09.06.2026): убрана верхняя полоса с
 * кнопкой-сворачиванием «⊗» (экономия высоты окна). Закрытие — свайпом пальцем ВНИЗ
 * по окну клавиатуры (порог SWIPE_CLOSE_PX) → `onClose`; открытие — по тапу в ячейку
 * (контроллер PartsTab). На ПК закрытие — кнопкой «✓ OK».
 */

/** Порог вертикального свайпа вниз для закрытия клавиатуры (правка #9). */
const SWIPE_CLOSE_PX = 50;

export interface NumericKeyboardProps {
  value: string;
  onChange: (newValue: string) => void;
  onNavigate?: (direction: 'up' | 'down' | 'left' | 'right') => void;
  onClose: () => void;
  visible: boolean;
  allowDecimal?: boolean;
  allowFraction?: boolean; // для дюймов; в мм-режиме false
}

export function NumericKeyboard({
  value,
  onChange,
  onNavigate,
  onClose,
  visible,
  allowDecimal = true,
  allowFraction = false,
}: NumericKeyboardProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const delRef = useRef<HTMLButtonElement>(null);
  const touchStartY = useRef<number | null>(null);

  // PR #11-FIX (CUT-3.x): при открытой клавиатуре нижние строки таблицы уходили ПОД
  // фиксированную клавиатуру (position:fixed; bottom:0) и не доскролливались. Решение:
  // публикуем РЕАЛЬНУЮ высоту клавиатуры в CSS-переменную `--cutting-kb-height` на
  // <body>; `.sc-table-wrap` берёт её как `padding-bottom` (запас снизу → нижние строки
  // скроллятся выше клавиатуры). Один сигнал на обе таблицы (Детали + Остатки) и любые
  // будущие — без дублирования. Высота динамическая (offsetHeight + ResizeObserver на
  // смену ориентации/вьюпорта), не хардкод. Десктоп (fine pointer) клавиатуру не
  // монтирует → переменная не ставится → padding-bottom:0 (десктоп не затронут, B-231).
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!visible || !el) {
      document.body.style.removeProperty('--cutting-kb-height');
      return;
    }
    const publish = () => {
      document.body.style.setProperty('--cutting-kb-height', `${el.offsetHeight}px`);
    };
    publish();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      document.body.style.removeProperty('--cutting-kb-height');
    };
  }, [visible]);

  // Свайп вниз → закрыть (правка #9). Жест на корне окна; порог SWIPE_CLOSE_PX.
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0]?.clientY ?? null;
  }, []);
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartY.current === null) return;
      const dy = (e.touches[0]?.clientY ?? 0) - touchStartY.current;
      if (dy > SWIPE_CLOSE_PX) {
        touchStartY.current = null;
        onClose();
      }
    },
    [onClose],
  );
  const handleTouchEnd = useCallback(() => {
    touchStartY.current = null;
  }, []);

  const handleDigit = useCallback((d: string) => onChange(value + d), [value, onChange]);

  const handleDecimal = useCallback(() => {
    if (!allowDecimal || value.includes('.')) return; // только одна точка
    onChange(value + '.');
  }, [value, onChange, allowDecimal]);

  const handleFraction = useCallback(() => {
    if (!allowFraction) return;
    onChange(value + '/');
  }, [value, onChange, allowFraction]);

  const handleDelete = useCallback(() => onChange(value.slice(0, -1)), [value, onChange]);
  const handleClear = useCallback(() => onChange(''), [onChange]);

  // Long-press на Del = очистить (numeric-keyboard.js:190-201). Cleanup слушателей.
  useEffect(() => {
    const btn = delRef.current;
    if (!btn) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const press = () => {
      timer = setTimeout(handleClear, 500);
    };
    const release = () => {
      if (timer) clearTimeout(timer);
    };
    btn.addEventListener('pointerdown', press);
    btn.addEventListener('pointerup', release);
    btn.addEventListener('pointerleave', release);
    return () => {
      if (timer) clearTimeout(timer);
      btn.removeEventListener('pointerdown', press);
      btn.removeEventListener('pointerup', release);
      btn.removeEventListener('pointerleave', release);
    };
  }, [handleClear]);

  if (!visible) return null;

  return (
    <div
      ref={rootRef}
      className="pomonshchik-keyboard visible"
      role="group"
      aria-label="Цифровая клавиатура"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Полоса-захват свайпа вниз для закрытия (правка #9) — заменяет прежнюю
          кнопку «⊗»; видимый индикатор жеста, не перехватывает тап по клавишам. */}
      <div className="kb-drag-handle" aria-hidden="true" />
      <div className="kb-grid">
        {/* Ряд 1: 1 2 3 ↑ */}
        <button type="button" onClick={() => handleDigit('1')}>
          1
        </button>
        <button type="button" onClick={() => handleDigit('2')}>
          2
        </button>
        <button type="button" onClick={() => handleDigit('3')}>
          3
        </button>
        <button
          type="button"
          className="kb-nav"
          data-key="up"
          aria-label="Ячейка выше"
          onClick={() => onNavigate?.('up')}
        >
          ↑
        </button>

        {/* Ряд 2: 4 5 6 ↓ */}
        <button type="button" onClick={() => handleDigit('4')}>
          4
        </button>
        <button type="button" onClick={() => handleDigit('5')}>
          5
        </button>
        <button type="button" onClick={() => handleDigit('6')}>
          6
        </button>
        <button
          type="button"
          className="kb-nav"
          data-key="down"
          aria-label="Ячейка ниже"
          onClick={() => onNavigate?.('down')}
        >
          ↓
        </button>

        {/* Ряд 3: 7 8 9 ← */}
        <button type="button" onClick={() => handleDigit('7')}>
          7
        </button>
        <button type="button" onClick={() => handleDigit('8')}>
          8
        </button>
        <button type="button" onClick={() => handleDigit('9')}>
          9
        </button>
        <button
          type="button"
          className="kb-nav"
          data-key="left"
          aria-label="Ячейка слева"
          onClick={() => onNavigate?.('left')}
        >
          ←
        </button>

        {/* Ряд 4: . 0 / → */}
        <button type="button" onClick={handleDecimal} disabled={!allowDecimal} aria-label="Точка">
          .
        </button>
        <button type="button" onClick={() => handleDigit('0')}>
          0
        </button>
        <button type="button" onClick={handleFraction} disabled={!allowFraction} aria-label="Дробь">
          /
        </button>
        <button
          type="button"
          className="kb-nav"
          data-key="right"
          aria-label="Ячейка справа"
          onClick={() => onNavigate?.('right')}
        >
          →
        </button>

        {/* Ряд 5: Del (2 ячейки) | ✓ OK (2 ячейки) */}
        <button
          type="button"
          ref={delRef}
          data-key="del"
          className="kb-del kb-wide"
          aria-label="Удалить символ"
          onClick={handleDelete}
        >
          Del
        </button>
        <button type="button" className="kb-ok kb-wide" aria-label="Готово" onClick={onClose}>
          ✓ OK
        </button>
      </div>
    </div>
  );
}
